/**
 * Keeps the camera store's `viewport` equal to the canvas container's size —
 * read once at mount, and again one animation frame after every resize.
 *
 * WHY THE FRAME. `setViewport` is a store write, and React flushes a store
 * write synchronously, in a microtask, TOGETHER with every React update still
 * queued at that moment (a sync update pulls the default and continuous lanes
 * in with it). Made from inside a ResizeObserver callback, that flush lands in
 * the middle of the observer's own delivery loop — and when one of the queued
 * updates changes this element's size, the browser reports "ResizeObserver
 * loop completed with undelivered notifications", once per frame for as long
 * as it goes on.
 *
 * The concrete case (2026-09): dragging a docked panel's grab edge queues a
 * width update per pointer move. The previous width had shrunk the canvas, so
 * the observer fired; the store write flushed the NEXT width; the panel moved
 * again under the observer, which had to skip the notification. One debug
 * report carried 34 of these, filed as "when generating thumbnails" — the
 * thumbnails panel had been widened to look at them.
 *
 * Deferred to the next animation frame, the callback changes no layout at all,
 * so the loop cannot form. The one frame of lag is invisible: every consumer
 * of the viewport carries a buffer (row windowing mounts a viewport of rows on
 * either side, the dot layer overdraws by three spacings, the toolbar and the
 * comment bubbles only clamp against it). The mount-time read stays
 * synchronous, so the first effects that place themselves against the
 * viewport (row windowing, the design-band framing, a share link's fit) see a
 * real size rather than 0×0.
 *
 * Pure DOM, no React: the pieces of the browser it uses are injectable, so the
 * deferral and the coalescing are unit-tested in node.
 */
import type { CanvasStore } from "./canvas.js";

/** The one thing the tracker reads off the container. */
export type ViewportElement = Pick<Element, "getBoundingClientRect">;

/** A `ResizeObserver` reduced to what the tracker needs (mockable in tests). */
export interface SizeObserverLike {
  observe(target: ViewportElement): void;
  disconnect(): void;
}

export interface ViewportTrackerDeps {
  createObserver(onResize: () => void): SizeObserverLike;
  requestFrame(callback: () => void): number;
  cancelFrame(handle: number): void;
}

/** The browser's own pieces — `window` methods wrapped so `this` stays right. */
function browserDeps(): ViewportTrackerDeps {
  return {
    createObserver: (onResize) =>
      new ResizeObserver(onResize) as unknown as SizeObserverLike,
    requestFrame: (callback) => window.requestAnimationFrame(callback),
    cancelFrame: (handle) => window.cancelAnimationFrame(handle),
  };
}

/**
 * Start tracking `el`; returns the stop function (an effect cleanup). The
 * current size is published at once; each resize is published on the next
 * animation frame, reading the size fresh at that point — several observer
 * callbacks before the frame collapse into one publish.
 */
export function trackViewport(
  el: ViewportElement,
  canvas: Pick<CanvasStore, "getState">,
  deps: ViewportTrackerDeps = browserDeps(),
): () => void {
  const publish = () => {
    const rect = el.getBoundingClientRect();
    canvas.getState().setViewport({ width: rect.width, height: rect.height });
  };
  publish();

  let frame: number | null = null;
  const observer = deps.createObserver(() => {
    if (frame !== null) return; // a frame is already on its way
    frame = deps.requestFrame(() => {
      frame = null;
      publish();
    });
  });
  observer.observe(el);

  return () => {
    observer.disconnect();
    if (frame !== null) deps.cancelFrame(frame);
    frame = null;
  };
}
